import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import type { Nullable } from "@msuite/utils/types";
import { client } from "@/hono";

/**
 * The chain of custody of a whole picking round — every carrier it shipped and every hand that
 * moved one, grouped by box.
 *
 * The round is the widest scope this can have. A `loadCarrierUnits` row is "box 3 of 18 on this
 * round" and nothing reuses one, so a carrier has no history beyond the shipment it was labelled
 * for.
 */
export function useSuborderTransportEvents(
	orderId: Nullable<string>,
	suborderId: Nullable<string>,
) {
	/** Query */
	const query = useQuery({
		queryKey: ["suborder", guardString(orderId), guardString(suborderId), "transport-events"],
		queryFn: async () => {
			const response = await client.stock.orders[":orderId"].suborders[":suborderId"][
				"transport-events"
			].$get({
				param: {
					orderId: guardString(orderId),
					suborderId: guardString(suborderId),
				},
			});
			const data = await responseHandler(response);
			return data.events;
		},
		enabled: !!orderId && !!suborderId,
	});

	/** Return */
	return { ...query, events: query.data };
}
